import { createElement, useState, type ImgHTMLAttributes } from 'react'
import type { AIChatOutputAsset } from '../api'
import { siteAssetVariantUrl } from '../api'
import {
  hasRichResponse,
  resolveAssetByOrdinal,
  type RichBlock,
  type RichCard,
  type RichMediaRef,
  type RichResponseV1,
} from './aiChatRichResponse'
import './ai-chat-rich-response.css'

function thumbUrl(workspaceId: string, asset: AIChatOutputAsset): string {
  return asset.thumbnail_url || siteAssetVariantUrl(workspaceId, asset.site_asset_id, 'thumbnail')
}

function fullUrl(workspaceId: string, asset: AIChatOutputAsset): string {
  return asset.download_url || siteAssetVariantUrl(workspaceId, asset.site_asset_id, 'original')
}

function LazyAssetImage({
  workspaceId,
  asset,
  alt,
  className,
}: {
  workspaceId: string
  asset: AIChatOutputAsset
  alt?: string
  className?: string
}) {
  const [useFull, setUseFull] = useState(false)
  const src = useFull ? fullUrl(workspaceId, asset) : thumbUrl(workspaceId, asset)
  const props: ImgHTMLAttributes<HTMLImageElement> = {
    src,
    alt: alt || `图片 ${asset.ordinal + 1}`,
    loading: 'lazy',
    className,
    onClick: () => setUseFull(true),
  }
  return <img {...props} />
}

function MediaFigure({
  workspaceId,
  assets,
  mediaRef,
}: {
  workspaceId: string
  assets: AIChatOutputAsset[]
  mediaRef: RichMediaRef
}) {
  const asset = resolveAssetByOrdinal(assets, mediaRef.ordinal)
  if (!asset) return null
  const role = mediaRef.asset_role || asset.asset_role || 'unknown'
  if (role === 'decorative' || role === 'provider_ui') return null
  return (
    <figure className={`tc-rich-media tc-rich-media-${role}`}>
      <LazyAssetImage workspaceId={workspaceId} asset={asset} alt={mediaRef.alt} className="tc-rich-media-img" />
      {role === 'generated' ? <figcaption className="tc-rich-media-cap">AI 生成图片</figcaption> : null}
    </figure>
  )
}

function CardView({
  workspaceId,
  assets,
  card,
}: {
  workspaceId: string
  assets: AIChatOutputAsset[]
  card: RichCard
}) {
  return (
    <article className="tc-rich-card">
      {card.title ? <h4 className="tc-rich-card-title">{card.title}</h4> : null}
      {card.subtitle ? <div className="tc-rich-card-subtitle">{card.subtitle}</div> : null}
      {card.body ? <p className="tc-rich-card-body">{card.body}</p> : null}
      {card.metadata && card.metadata.length > 0 ? (
        <dl className="tc-rich-card-meta">
          {card.metadata.map((row, i) => (
            <div key={i} className="tc-rich-card-meta-row">
              {row.label ? <dt>{row.label}</dt> : null}
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {card.media_refs?.map((m, i) => (
        <MediaFigure key={`${m.ordinal}-${i}`} workspaceId={workspaceId} assets={assets} mediaRef={m} />
      ))}
      {card.links && card.links.length > 0 ? (
        <div className="tc-rich-card-links">
          {card.links.map((l, i) => (
            <a key={i} href={l.href} target="_blank" rel="noopener noreferrer">
              {l.label || l.href}
            </a>
          ))}
        </div>
      ) : null}
    </article>
  )
}

function BlockView({
  workspaceId,
  assets,
  block,
}: {
  workspaceId: string
  assets: AIChatOutputAsset[]
  block: RichBlock
}) {
  switch (block.type) {
    case 'heading': {
      const level = Math.min(6, Math.max(1, block.level || 2))
      return createElement(`h${level}`, { className: 'tc-rich-heading' }, block.text)
    }
    case 'paragraph':
    case 'list_item':
      return <p className="tc-rich-paragraph">{block.text}</p>
    case 'quote':
      return <blockquote className="tc-rich-quote">{block.text}</blockquote>
    case 'code':
      return (
        <pre className="tc-rich-code">
          <code>{block.text}</code>
        </pre>
      )
    case 'list': {
      const Tag = block.list?.ordered ? 'ol' : 'ul'
      return (
        <Tag className="tc-rich-list">
          {(block.list?.items || []).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </Tag>
      )
    }
    case 'table': {
      const headers = block.table?.headers || []
      const rows = block.table?.rows || []
      return (
        <div className="tc-rich-table-wrap">
          <table className="tc-rich-table">
            {headers.length > 0 ? (
              <thead>
                <tr>
                  {headers.map((h, i) => (
                    <th key={i}>{h}</th>
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }
    case 'link':
      return block.link ? (
        <p className="tc-rich-link">
          <a href={block.link.href} target="_blank" rel="noopener noreferrer">
            {block.link.label || block.link.href}
          </a>
        </p>
      ) : null
    case 'image':
      return block.media ? <MediaFigure workspaceId={workspaceId} assets={assets} mediaRef={block.media} /> : null
    case 'image_group':
      return (
        <div className="tc-rich-image-group">
          {(block.media_refs || []).map((m, i) => (
            <MediaFigure key={`${m.ordinal}-${i}`} workspaceId={workspaceId} assets={assets} mediaRef={m} />
          ))}
        </div>
      )
    case 'card':
      return block.card ? <CardView workspaceId={workspaceId} assets={assets} card={block.card} /> : null
    case 'card_group':
      return (
        <div className="tc-rich-card-group">
          {(block.cards || []).map((c, i) => (
            <CardView key={i} workspaceId={workspaceId} assets={assets} card={c} />
          ))}
        </div>
      )
    case 'divider':
      return <hr className="tc-rich-divider" />
    case 'unknown':
      return block.fallback_text || block.text ? (
        <p className="tc-rich-unknown">{block.fallback_text || block.text}</p>
      ) : null
    default:
      return null
  }
}

/**
 * Unified Tenant renderer for Rich Response V1.
 * Provider-agnostic — never branches on ChatGPT/Gemini/Grok DOM.
 */
export function RichResponseRenderer({
  workspaceId,
  richResponse,
  assets,
}: {
  workspaceId: string
  richResponse: RichResponseV1
  assets: AIChatOutputAsset[]
}) {
  if (!hasRichResponse(richResponse)) return null
  return (
    <div className="tc-rich-response" data-rich-version={richResponse.version}>
      {richResponse.blocks.map((block, i) => (
        <div key={block.id || `b-${i}`} className="tc-rich-block" data-block-type={block.type}>
          <BlockView workspaceId={workspaceId} assets={assets} block={block} />
        </div>
      ))}
    </div>
  )
}
